import { toast } from '@eleo-rh/core-ui/contexts'
import { API_URL_ASYNC_EXPORT } from 'constants/api'
import { exportStatusKey, useAsyncNotification } from 'context/AsyncNotificationProvider'
import { useQuery } from 'context/QueryProvider'
import { type ReactNode, useCallback, useEffect, useState } from 'react'

export type ExportStatus = {
  status?: 'none' | 'started' | 'inProgress' | 'pending' | 'failed' | 'done' | 'ready' | null
  statusMessage?: string | null
  message?: string | null
  total?: number
  processed?: number
  url?: string | null
  document?: { url?: string | null } | null
  exportConfigurable?: string | null
  exportTaskId?: number
}

export type RequestOptions = {
  method?: string
  data?: Record<string, unknown>
  params?: Record<string, unknown>
}

interface AsyncExportProps {
  requestURL: string
  exportType: string
  exportConfigurable?: string
  exportName?: string
  elementId?: number | string
  children: (props: {
    startExport: (options?: RequestOptions) => Promise<boolean>
    status?: ExportStatus
    reset: () => void
  }) => ReactNode
}

function AsyncExport({
  requestURL,
  exportType,
  exportConfigurable,
  exportName,
  elementId,
  children,
}: AsyncExportProps) {
  const { client } = useQuery()
  const { exportStatuses, resetExportStatus } = useAsyncNotification()
  const [status, setStatus] = useState<ExportStatus>()
  const exportKey = exportStatusKey(exportConfigurable ?? exportType, elementId)
  const exportStatus = exportStatuses[exportKey]

  const reset = useCallback(() => {
    resetExportStatus(exportKey, exportStatus?.exportTaskId)
    setStatus({ status: 'none', statusMessage: null, url: null })
  }, [exportKey, exportStatus?.exportTaskId, resetExportStatus])

  useEffect(() => {
    if (!exportStatus) {
      setStatus(current => (current?.exportTaskId ? { status: 'none', statusMessage: null, url: null } : current))
      return
    }

    switch (exportStatus.status) {
      case 'started':
        setStatus({ status: 'started', exportTaskId: exportStatus.exportTaskId })
        break
      case 'inProgress':
        setStatus({
          status: 'pending',
          total: exportStatus.total,
          processed: exportStatus.processed,
          exportTaskId: exportStatus.exportTaskId,
        })
        break
      case 'failed':
        setStatus({ status: 'failed', statusMessage: exportStatus.message, exportTaskId: exportStatus.exportTaskId })
        break
      case 'done':
        setStatus({
          status: 'ready',
          url: API_URL_ASYNC_EXPORT.replace('ID', String(exportStatus.exportTaskId)),
          exportTaskId: exportStatus.exportTaskId,
        })
        break
    }
  }, [exportStatus])

  const startExport = async (options: RequestOptions = {}) => {
    resetExportStatus(exportKey, exportStatus?.exportTaskId)
    setStatus({ status: 'started' })
    try {
      await client.request(requestURL, {
        ...options,
        data: {
          ...options.data,
          ...(exportName ? { exportName } : {}),
        },
      })
      return true
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setStatus({ status: 'failed', message })
      toast.error({ title: message, color: 'red' })
      return false
    }
  }

  return children({ startExport, status, reset })
}

export default AsyncExport
